import { Checkbox, Col, Divider, Form, Input, message, Modal, Row, Spin, Tag } from 'antd';
import React from 'react';
import { ITableRef } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { formatApiDateTime, formatDisplayDate, ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { SaveOutlined } from '@ant-design/icons';
import SeatingModelInput from '@/components/FormItems/SeatingModelInput';
import TripGroupInput from '@/components/FormItems/TripGroupInput';
import AppDateTimePicker from '@/components/FormItems/DateTimePicker';
import { DayOfWeekType } from '@/constants';
import { range } from 'lodash';
import moment from 'moment';
import { apiSdk } from '@/httpClient';
type Props = {
  onClose: (reload: boolean) => void;
  tripId: string;
};

export default function CopyTripModal({ onClose, tripId }: Props) {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  React.useEffect(() => {
    if (tripId) {
      setLoading(true);
      apiSdk.TripService.getDetails({ id: tripId })
        .then(res => {
          form.setFieldsValue({ ...res, tripName: res.tripName + ' [copy]', plannedDepartureDateTime: null });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [tripId]);

  const isRecurred = Form.useWatch('isRecurred', form);
  const plannedDepartureDateTime = Form.useWatch('plannedDepartureDateTime', form);
  const recurrenceNumDays: number[] = Form.useWatch('recurrenceNumDays', form);
  const recurrenceDates = React.useMemo(() => {
    if (isRecurred && plannedDepartureDateTime && recurrenceNumDays?.length) {
      const dates: string[] = [];
      range(1, 31).forEach(i => {
        let date = moment().add(i, 'day');
        let numOfWeek = date.day();
        if (recurrenceNumDays.includes(numOfWeek)) {
          dates.push(formatDisplayDate(date));
        }
      });
      return dates;
    }
    return [];
  }, [isRecurred, plannedDepartureDateTime, recurrenceNumDays]);

  const onSubmit = values => {
    setLoading(true);
    apiSdk.TripService.duplicateTrip({
      body: {
        ...values,
        plannedDepartureDateTime: formatApiDateTime(values['plannedDepartureDateTime']),
        tripId: tripId,
      },
    })
      .then(response => {
        if (response?.success) {
          zTb.current?.reload();
          onClose(true);
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal
      style={{ top: 10 }}
      open={true}
      maskClosable={false}
      width={ModalWidth.small}
      className="nta-custom-form create-edit-trip-modal"
      title={t('tripsPage_CopyTrip')}
      forceRender={true}
      okText={t('save')}
      cancelText={t('cancel')}
      okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
      onOk={() => {
        form.validateFields().then(onSubmit);
      }}
      onCancel={() => {
        onClose(false);
      }}
    >
      <Spin spinning={loading}>
        <Form form={form} layout="horizontal" labelCol={{ md: 8 }} labelAlign="right">
          <Row gutter={ROW_GUTTER}>
            <Col md={24}>
              <Form.Item label={t('tripsPage_TripName')} name="tripName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Input />
              </Form.Item>
              <Form.Item label={t('tripsPage_SeatingModel')} name="seatingModelId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <SeatingModelInput />
              </Form.Item>
              <Form.Item
                label={t('tripsPage_DepartureDateTime')}
                name="plannedDepartureDateTime"
                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
              >
                <AppDateTimePicker className="w-100" />
              </Form.Item>
              <Form.Item label={t('tripsPage_Group')} name="tripGroupId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <TripGroupInput />
              </Form.Item>
              <Form.Item label="  " name="isRecurred" colon={false} valuePropName="checked">
                <Checkbox>{t('tripsPage_WantToRecurTrip')}</Checkbox>
              </Form.Item>
              {isRecurred ? (
                <>
                  <div dangerouslySetInnerHTML={{ __html: t('tripsPage_RecurTripNote') }} />
                  <b>{t('tripsPage_TripRecurrenceDays')}</b>
                  <Form.Item name="recurrenceNumDays">
                    <Checkbox.Group options={DayOfWeekType.__getSelectOptions()} />
                  </Form.Item>
                  <Divider />
                  {recurrenceDates.map(date => {
                    return (
                      <Tag key={date} color="blue" style={{ marginBottom: 2, marginRight: 4 }}>
                        {date}
                      </Tag>
                    );
                  })}
                </>
              ) : null}
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
}
